import { useState, useEffect } from "react";
import { getFirmConfig, getFirmsData } from "@/shared/lib/utils/firm";
import { resolveFirmBranding } from "@/shared/lib/utils/branding";
import tifinGiveMasterLogo from "@/shared/assets/logos/tifin_give_master_logo.svg";
import RBTLogo from "@/shared/assets/logos/rbtLogo.svg";
import AMKLogo from "@/shared/assets/logos/amkLogo.png";
import giveWhiteLogo from "@/shared/assets/logos/giveLogoWhite.svg";
import mswLogo from "@/shared/assets/logos/mswLogo.svg";
import { useUserStore } from "../store/userStore";

/**
 * Hook to get firm logo from getFirmConfig
 * @returns {string} The firm logo URL
 */

const getDefaultDarkLogo = (programName: string | undefined) => {
  if (!programName) {
    return tifinGiveMasterLogo;
  }

  switch (programName) {
    case "AMK":
      return AMKLogo;
    case "RBT":
      return RBTLogo;
    case "MSW":
      return mswLogo;
    case "TG":
      return tifinGiveMasterLogo;
    default:
      return tifinGiveMasterLogo;
  }
};

export const getDefaultLightLogo = (programName: string | undefined) => {
  if (!programName) {
    return giveWhiteLogo;
  }

  switch (programName) {
    case "AMK":
      return AMKLogo;
    case "RBT":
      return RBTLogo;
    case "MSW":
      return mswLogo;
    case "TG":
      return giveWhiteLogo;
    default:
      return giveWhiteLogo;
  }
};

interface FirmLogoProps {
  fallback?: string;
  useLightLogo?: boolean;
  /**
   * Resolve this firm instead of the logged-in user's. For pre-login screens
   * that know the firm from somewhere other than a session — the registration
   * flow, for instance, gets it from the invite token — where the domain logo
   * would otherwise be the only thing available.
   */
  firmId?: string | number | null;
}

export const useFirmLogo = ({
  fallback = getDefaultDarkLogo(process.env.NEXT_PROGRAM_NAME || ""),
  firmId,
}: FirmLogoProps = {}) => {
  const user = useUserStore((state) => state.user);
  const effectiveFirmId = firmId ?? user?.firm?.id;
  const [firmLogo, setFirmLogo] = useState<string>(fallback);
  const [isUsingFallback, setIsUsingFallback] = useState<boolean>(true);

  /**
   * Fall back to the program logo when the firm's own logo fails to load — a broken
   * or unreachable asset would otherwise leave an empty gap in the app chrome.
   * Pass to `onError` on any <Image> rendering `firmLogo`.
   */
  const handleLogoError = (): void => {
    setFirmLogo(fallback);
    setIsUsingFallback(true);
  };

  useEffect(() => {
    if (effectiveFirmId) {
      // The firm's logo, inheriting the parent enterprise's and then the
      // domain's logo when the firm has none; logoUrl from the user query is
      // the last backend-provided source before the static fallback.
      const resolved = resolveFirmBranding(effectiveFirmId, getFirmsData(), {
        logo: getFirmConfig(window.location.hostname, "logo", ""),
      });
      // Only fall back to the user query's logoUrl when it describes this same
      // firm — an explicit firmId must never borrow another firm's artwork.
      const logo =
        resolved.logo ??
        (firmId ? undefined : user?.firm?.logoUrl) ??
        undefined;
      if (logo) {
        setFirmLogo(logo);
        setIsUsingFallback(false);
      }
    } else {
      const logo = getFirmConfig(window.location.hostname, "logo", fallback);
      setFirmLogo(logo);
      // If getFirmConfig returns the fallback, it means no backend logo was found
      setIsUsingFallback(logo === fallback);
    }
  }, [fallback, user, firmId, effectiveFirmId]);
  return { firmLogo, isUsingFallback, handleLogoError };
};
